<div class="onboarding-report-builder-page">
  <div v-if="total_loading" class="onboarding-report-loader">
    <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
    <span>{{ page_loading_message }}</span>
    <small>{{ page_loading_detail }}</small>
  </div>

  <main class="onboarding-report-builder-panel">
    <section class="onboarding-report-page-header">
      <div>
        <div class="onboarding-report-breadcrumb text-uppercase d-flex align-items-center gap-2">
          <span>Insight</span>
          <span>/</span>
          <span>Reports</span>
        </div>
        <h1 class="onboarding-report-title">Reports</h1>
      </div>
    </section>

    <section class="onboarding-report-tab-panel">
      <section class="onboarding-report-filters">
        <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
          <h2 class="mb-0">Report Filters</h2>
          <div class="onboarding-report-actions">
            <button
              type="button"
              class="btn btn-light border secondary-color d-inline-flex align-items-center justify-content-center gap-2"
              :disabled="checking_report"
              @click="check_report"
            >
              <i class="bi bi-check2-circle"></i>
              <span>{{ checking_report ? 'Checking...' : 'Check' }}</span>
            </button>
            <button
              type="button"
              class="btn btn-light border secondary-color d-inline-flex align-items-center justify-content-center gap-2"
              :disabled="previewing_report"
              @click="preview_report"
            >
              <i class="bi bi-table"></i>
              <span>{{ previewing_report ? 'Loading...' : 'Preview' }}</span>
            </button>
            <button
              type="button"
              class="btn btn-primary-dark d-inline-flex align-items-center justify-content-center gap-2"
              :disabled="generating_report"
              @click="generate_report"
            >
              <i class="bi bi-download"></i>
              <span>{{ generating_report ? 'Generating...' : 'Generate' }}</span>
            </button>
          </div>
        </div>

        <div class="onboarding-report-filter-grid">
          <label class="onboarding-report-field">
            <span>Start Date</span>
            <input v-model="report_form.start_date" type="date" class="form-control onboarding-report-control">
          </label>

          <label class="onboarding-report-field">
            <span>End Date</span>
            <input v-model="report_form.end_date" type="date" class="form-control onboarding-report-control">
          </label>

          <label class="onboarding-report-field">
            <span>Journey</span>
            <select v-model="report_form.journey" class="form-select onboarding-report-control">
              <option value="">All Journeys</option>
              <option
                v-for="option in journey_options"
                :key="`journey-${option.id}`"
                :value="String(option.id)"
              >
                {{ option.name }}
              </option>
            </select>
          </label>

          <label class="onboarding-report-field">
            <span>Status</span>
            <select v-model="report_form.status" class="form-select onboarding-report-control">
              <option value="">All Statuses</option>
              <option
                v-for="option in status_options"
                :key="`status-${option.id}`"
                :value="String(option.id)"
              >
                {{ option.name }}
              </option>
            </select>
          </label>

          <label class="onboarding-report-field">
            <span>Output Format</span>
            <select v-model="selected_format" class="form-select onboarding-report-control">
              <option
                v-for="option in output_format_options"
                :key="`output-${option}`"
                :value="option"
              >
                {{ output_format_label(option) }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <div v-if="report_error" class="onboarding-report-alert">
        {{ report_error }}
      </div>

      <section class="onboarding-report-fields">
        <div class="onboarding-report-section-head mb-3">
          <span>Select Fields to Include in Excel</span>
          <strong>{{ selected_field_count }}</strong>
        </div>

        <div v-if="selected_fields.length" class="onboarding-report-check-grid">
          <label
            v-for="field in selected_fields"
            :key="`field-${field.key}`"
            class="onboarding-report-check"
            :class="{ 'is-selected': is_report_field_selected(field.key) }"
          >
            <input
              type="checkbox"
              :checked="is_report_field_selected(field.key)"
              @change="toggle_report_field(field.key)"
            >
            <span>
              <strong>{{ field.label }}</strong>
              <small>{{ field.meta }}</small>
            </span>
          </label>
        </div>

        <div v-else class="onboarding-report-empty">
          No report fields have been configured yet.
        </div>
      </section>

      <section class="onboarding-report-preview">
        <div class="onboarding-report-section-head mb-3">
          <span>Preview</span>
          <strong>{{ preview_count }} row{{ preview_count === 1 ? '' : 's' }}</strong>
        </div>

        <div v-if="previewing_report" class="onboarding-report-loading">
          <span class="spinner-border spinner-border-sm"></span>
          <span>Loading preview...</span>
        </div>

        <div v-else-if="visible_preview_rows.length" class="table-responsive onboarding-report-table-wrap">
          <table class="table table-sm table-hover onboarding-report-table">
            <thead>
              <tr>
                <th v-for="column in report_columns" :key="`preview-head-${column.key}`">
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, row_index) in visible_preview_rows" :key="`preview-row-${row_index}`">
                <td v-for="column in report_columns" :key="`preview-cell-${row_index}-${column.key}`">
                  <span :title="row_value(row, column.key)">{{ row_value(row, column.key) }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="onboarding-report-pagination">
            <span class="onboarding-report-pagination-summary">
              {{ preview_page_start }}-{{ preview_page_end }} of {{ preview_count }}
            </span>
            <div v-if="preview_page_count > 1" class="d-inline-flex align-items-center rounded-3 border overflow-hidden bg-white">
              <button type="button" class="btn btn-sm btn-light border-0 rounded-0 px-2" :disabled="bounded_preview_page === 1" aria-label="First preview page" @click="set_preview_page(1)">
                <i class="bi bi-chevron-double-left"></i>
              </button>
              <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2" :disabled="bounded_preview_page === 1" aria-label="Previous preview page" @click="set_preview_page(bounded_preview_page - 1)">
                <i class="bi bi-chevron-left"></i>
              </button>
              <button
                v-for="page in preview_page_numbers"
                :key="`preview-page-${page}`"
                type="button"
                class="btn btn-sm rounded-0 border-0 border-start px-3"
                :class="page === bounded_preview_page ? 'btn-primary-dark text-white' : 'btn-light'"
                :aria-current="page === bounded_preview_page ? 'page' : null"
                @click="set_preview_page(page)"
              >
                {{ page }}
              </button>
              <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2" :disabled="bounded_preview_page === preview_page_count" aria-label="Next preview page" @click="set_preview_page(bounded_preview_page + 1)">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2" :disabled="bounded_preview_page === preview_page_count" aria-label="Last preview page" @click="set_preview_page(preview_page_count)">
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-else class="onboarding-report-empty">
          No preview rows loaded.
        </div>
      </section>

      <section class="onboarding-report-history">
        <div class="onboarding-report-section-head mb-3">
          <span>Generated Reports</span>
        </div>

        <div v-if="loading_report_history" class="onboarding-report-loading">
          <span class="spinner-border spinner-border-sm"></span>
          <span>Loading generated reports...</span>
        </div>

        <div v-else-if="generated_history.length" class="onboarding-report-history-list">
          <article
            v-for="report in paginated_history_rows"
            :key="`generated-report-${report.report_id || report.id}`"
            class="onboarding-report-history-row"
          >
            <div>
              <div class="onboarding-report-history-title-row">
                <strong>{{ generated_report_title(report) }}</strong>
                <span class="onboarding-report-format-badge" :class="generated_report_format_class(report)">
                  {{ generated_report_format_label(report) }}
                </span>
              </div>
              <span>{{ generated_report_meta(report) }}</span>
            </div>
            <button
              type="button"
              class="btn btn-light border secondary-color d-inline-flex align-items-center justify-content-center gap-2"
              @click="open_saved_generated_report(report)"
            >
              <i class="bi bi-box-arrow-up-right"></i>
              <span>Open</span>
            </button>
          </article>

          <div class="onboarding-report-pagination">
            <span class="onboarding-report-pagination-summary">
              {{ history_page_start }}-{{ history_page_end }} of {{ generated_history.length }}
            </span>
            <div v-if="history_page_count > 1" class="d-inline-flex align-items-center rounded-3 border overflow-hidden bg-white">
              <button type="button" class="btn btn-sm btn-light border-0 rounded-0 px-2" :disabled="bounded_history_page === 1" aria-label="First history page" @click="set_history_page(1)">
                <i class="bi bi-chevron-double-left"></i>
              </button>
              <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2" :disabled="bounded_history_page === 1" aria-label="Previous history page" @click="set_history_page(bounded_history_page - 1)">
                <i class="bi bi-chevron-left"></i>
              </button>
              <button
                v-for="page in history_page_numbers"
                :key="`history-page-${page}`"
                type="button"
                class="btn btn-sm rounded-0 border-0 border-start px-3"
                :class="page === bounded_history_page ? 'btn-primary-dark text-white' : 'btn-light'"
                :aria-current="page === bounded_history_page ? 'page' : null"
                @click="set_history_page(page)"
              >
                {{ page }}
              </button>
              <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2" :disabled="bounded_history_page === history_page_count" aria-label="Next history page" @click="set_history_page(bounded_history_page + 1)">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2" :disabled="bounded_history_page === history_page_count" aria-label="Last history page" @click="set_history_page(history_page_count)">
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-else class="onboarding-report-empty">
          No generated reports found.
        </div>
      </section>
    </section>
  </main>

  <div
    v-if="generated_report_preview_open"
    class="onboarding-generated-report-preview-backdrop"
    role="dialog"
    aria-modal="true"
    @click.self="close_generated_report_preview"
  >
    <section class="onboarding-generated-report-preview-modal">
      <header class="onboarding-generated-report-preview-header">
        <div>
          <h2>{{ generated_report_preview_title }}</h2>
          <span>{{ generated_report_preview_type === 'pdf' ? 'PDF preview' : 'Excel preview' }}</span>
        </div>
        <button
          type="button"
          class="btn btn-light border onboarding-generated-report-preview-close"
          aria-label="Close preview"
          @click="close_generated_report_preview"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="onboarding-generated-report-preview-body">
        <div v-if="loading_generated_report_preview" class="onboarding-report-loading">
          <span class="spinner-border spinner-border-sm"></span>
          <span>Opening report...</span>
        </div>

        <div v-else-if="generated_report_preview_error" class="onboarding-report-empty">
          {{ generated_report_preview_error }}
        </div>

        <div v-else-if="generated_report_preview_type === 'pdf' && generated_report_preview_url" class="onboarding-generated-report-preview-pdf-wrap">
          <iframe class="onboarding-generated-report-preview-pdf-frame" :src="generated_report_preview_url" :title="generated_report_preview_title"></iframe>
        </div>

        <div v-else-if="generated_report_preview_type === 'excel' && generated_report_preview_rows.length" class="table-responsive onboarding-report-table-wrap onboarding-generated-report-preview-table-wrap">
          <table class="table table-sm table-hover onboarding-report-table">
            <thead>
              <tr>
                <th v-for="column in generated_report_preview_columns" :key="`generated-preview-head-${column.key}`">
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, row_index) in generated_report_preview_rows" :key="`generated-preview-row-${row_index}`">
                <td v-for="column in generated_report_preview_columns" :key="`generated-preview-cell-${row_index}-${column.key}`">
                  <span :title="row_value(row, column.key)">{{ row_value(row, column.key) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="onboarding-report-empty">
          No preview is available for this generated report.
        </div>
      </div>
    </section>
  </div>
</div>
